import { MAX_PERIODS_BACK } from "@pax/api-contract/accounting/v1alpha1";
import { focusHaloFlush, panelMotion, press } from "@pax/ui";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const stepButton = `grid size-6 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground disabled:pointer-events-none disabled:opacity-30 ${panelMotion} ${press} ${focusHaloFlush}`;

/**
 * Steps a chart back and forward through periods as long as its range, `back` periods from the
 * newest, with the period shown between. Next stops at the newest; Now jumps back to it.
 */
export function PeriodStepper({
  back,
  label,
  onChange,
  furthest = MAX_PERIODS_BACK,
}: {
  back: number;
  /** The period shown, or null while it is unknown. */
  label: string | null;
  onChange: (back: number) => void;
  /** The most periods back there is anything to show. */
  furthest?: number;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-full bg-foreground/5 p-0.5 text-xs">
      <button
        type="button"
        aria-label="Previous period"
        title="Previous period"
        disabled={back >= furthest}
        onClick={() => onChange(back + 1)}
        className={stepButton}
      >
        <ChevronLeft className="size-3.5" aria-hidden />
      </button>
      <span aria-live="polite" className="min-w-0 px-1 whitespace-nowrap tabular-nums">
        {label ?? "…"}
      </span>
      <button
        type="button"
        aria-label="Next period"
        title="Next period"
        disabled={back === 0}
        onClick={() => onChange(back - 1)}
        className={stepButton}
      >
        <ChevronRight className="size-3.5" aria-hidden />
      </button>
      {back > 0 && (
        <button
          type="button"
          onClick={() => onChange(0)}
          className={`shrink-0 rounded-full px-2.5 py-0.5 font-medium text-muted-foreground hover:bg-background hover:text-foreground ${panelMotion} ${press} ${focusHaloFlush}`}
        >
          Now
        </button>
      )}
    </div>
  );
}

/** How many periods back an address asks for, when it is a whole number in reach. */
export function periodsBackFrom(value: unknown) {
  const back = Number(value);
  return Number.isInteger(back) && back > 0 && back <= MAX_PERIODS_BACK ? back : undefined;
}
